import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { useConversas } from '@/conversas/ConversasProvider';
import { ouvirMensagens, reenviarMensagem } from '@/services/conversasService';
import { explicacaoDaEntrega, podeReenviar, rotuloDaEntrega, temAvisoDeEntrega } from '@/conversas/entrega';
import MidiaMensagem from './MidiaMensagem';
import AvatarContato from './AvatarContato';
import { nomeDoContato } from '@/conversas/contato';
import TextoComLinks, { CapaYoutube } from './TextoComLinks';
import { agruparReacoes, nomesDoChip } from '@/conversas/reacoes';
import { comDivisorias } from '@/conversas/dias';
import DialogoEncaminhar from './DialogoEncaminhar';
import BotaoReagir from './BotaoReagir';

const hora = (valor) => {
  const data = typeof valor?.toDate === 'function' ? valor.toDate() : null;
  return data ? format(data, 'dd/MM HH:mm') : '';
};

const STATUS_ENVIO = {
  enviando: '⋯',
  enviada: '✓',
  entregue: '✓✓',
  lida: '✓✓',
  falhou: '!',
};

/**
 * Os emojis grudados na mensagem, como no WhatsApp.
 *
 * Uma fileira discreta no rodapé da bolha — NÃO uma bolha própria. Reação não é
 * mensagem: dar-lhe uma linha na thread foi exatamente o defeito que esta leva
 * consertou (cada emoji virava um "Mensagem não suportada" fantasma).
 *
 * Devolve `null` sem reações, então pode ser chamado sem condicional em volta.
 */
function SelosDeReacao({ reacoes }) {
  const grupos = agruparReacoes(reacoes);
  if (grupos.length === 0) return null;

  return (
    <div className="msg-reacoes">
      {grupos.map((g) => (
        <span key={g.emoji} className="chip-reacao" title={nomesDoChip(g)}>
          <span className="chip-reacao-emoji">{g.emoji}</span>
          {/* A contagem só aparece a partir de 2: "❤️ 1" é ruído, porque um
              selo sozinho já significa uma pessoa. */}
          {g.contagem > 1 && <span className="chip-reacao-n">{g.contagem}</span>}
        </span>
      ))}
    </div>
  );
}

function Bolha({ mensagem, aoEncaminhar, ehGrupo, contato, aoReenviar, reenviando }) {
  const { direcao, interna, tipo, apagadaPeloCliente, remetente } = mensagem;

  /**
   * Marcador de sistema — pílula centralizada, como a divisória de dia.
   *
   * Vem ANTES do ramo `interna` de propósito: o "transferiu" do
   * `transferirConversa` já era `tipo: 'sistema'` mas nascia `interna: true`, e
   * os marcadores de ciclo nascem `interna: false`. Tratando o tipo primeiro,
   * os dois caem no mesmo desenho — antes, o mesmo evento apareceria de dois
   * jeitos conforme o valor de `interna`.
   *
   * Não é bolha: um evento de ciclo não tem lado, não se encaminha e não recebe
   * reação. Por isso nada do rodapé da bolha aparece aqui.
   */
  if (tipo === 'sistema') {
    return (
      <li className="msg-ciclo">
        <span>
          {mensagem.texto}
          <time>{hora(mensagem.enviadaEm)}</time>
        </span>
      </li>
    );
  }

  // Sussurro tem visual claramente distinto (fundo amarelo): a confusão entre
  // nota interna e mensagem ao cliente é o erro mais caro de um inbox.
  if (interna) {
    return (
      <li className="msg nota">
        <div className="msg-corpo">
          {/* `tipo: 'sistema'` já saiu acima, na pílula centralizada. O que
              chega aqui é sempre sussurro da equipe. */}
          <span className="msg-etiqueta">Nota interna — o cliente não vê</span>
          {/* Links clicáveis também na nota: colar um endereço para o colega
              ver é justamente o que se faz num sussurro. A CAPA do YouTube fica
              de fora daqui — a nota é um bilhete curto, e um cartão de vídeo
              nela pesaria mais que o recado. */}
          <TextoComLinks texto={mensagem.texto} />
          <span className="msg-meta">
            {remetente?.nome} · {hora(mensagem.enviadaEm)}
          </span>
        </div>
      </li>
    );
  }

  const daClinica = direcao === 'saida';

  /**
   * Avatar ao lado do ÁUDIO recebido, e só dele.
   *
   * O áudio é a única mídia que não mostra quem falou: imagem e vídeo trazem o
   * conteúdo, o texto se lê, mas uma barra de player é igual para todo mundo. A
   * foto ali devolve o rosto a uma bolha que, sozinha, é anônima.
   *
   * A foto vem da CONVERSA, nunca da mensagem — mensagem não guarda foto.
   *
   * ⚠ Em GRUPO não aparece: não há foto por participante (o `participant` é um
   * `@lid` opaco), e usar a foto do grupo faria parecer que todos os áudios são
   * da mesma pessoa. Melhor nada que uma cara errada.
   */
  const comAvatar = !daClinica && !ehGrupo && tipo === 'audio' && Boolean(contato);

  return (
    <li className={daClinica ? 'msg saida' : 'msg entrada'}>
      {comAvatar && (
        <AvatarContato foto={contato.foto} nome={contato.nome} tamanho={30} />
      )}
      <div className={apagadaPeloCliente ? 'msg-corpo apagada' : 'msg-corpo'}>
        {apagadaPeloCliente && (
          // Anti-apagamento: o conteúdo continua visível para a equipe.
          //
          // Com mídia, o texto muda para "arquivo": uma foto apagada continua
          // aparecendo inteira, e "conteúdo preservado" sobre uma imagem
          // visível não diz que aquela imagem é a que o paciente tentou tirar
          // da conversa.
          <span className="msg-etiqueta apagada">
            {mensagem.midia
              ? 'Apagada pelo cliente — arquivo preservado'
              : 'Apagada pelo cliente — conteúdo preservado'}
          </span>
        )}

        {/* Encaminhada de outra conversa. Sem o selo, uma foto que veio de
            outro paciente parece ter sido tirada aqui. */}
        {mensagem.encaminhada && <span className="msg-encaminhada">↪ Encaminhada</span>}

        {mensagem.midia && <MidiaMensagem mensagem={mensagem} />}

        {/* Com mídia na tela, o texto é legenda: só aparece se existir. Sem
            mídia, "(sem texto)" continua sinalizando a mensagem vazia. */}
        {mensagem.texto ? (
          <>
            <TextoComLinks texto={mensagem.texto} />
            {/* A capa vem DEPOIS do texto, como no WhatsApp: quem manda o link
                quase sempre escreve algo antes dele. Só aparece se houver
                vídeo, então não precisa de condicional aqui. */}
            <CapaYoutube texto={mensagem.texto} />
          </>
        ) : (
          !mensagem.midia && (
            <p>
              <em>(sem texto)</em>
            </p>
          )
        )}

        {/* Abaixo do texto e da mídia, acima da hora — a mesma posição do
            WhatsApp. Vale para entrada e saída, 1:1 e grupo. */}
        <SelosDeReacao reacoes={mensagem.reacoes} />

        {/* ★ WB — a mensagem que NÃO saiu fica aqui, marcada, com o motivo que o servidor redigiu. Quem abrir
            a conversa amanhã vê que houve tentativa frustrada; "incerta" (pode ter saído) avisa e NÃO
            oferece reenvio, porque repetir duplicaria a mensagem do paciente.
            ★ WC-c1: o botão "Tentar de novo" está DESLIGADO por `REENVIO_DISPONIVEL` — a callable
            `reenviarMensagem` saiu do ar na reversão do incidente. O título e a explicação FICAM: o rastro
            da falha é a razão de ser da peça; o que sai é só a ação que o servidor não tem. */}
        {temAvisoDeEntrega(mensagem) && (
          <span className="msg-entrega">
            {rotuloDaEntrega(mensagem)}
            {explicacaoDaEntrega(mensagem) && (
              <span className="msg-entrega-explicacao">{explicacaoDaEntrega(mensagem)}</span>
            )}
            {podeReenviar(mensagem) && (
              <button type="button" className="msg-reenviar" onClick={() => aoReenviar(mensagem)} disabled={reenviando}>
                {reenviando ? 'Enviando…' : 'Tentar de novo'}
              </button>
            )}
          </span>
        )}

        <span className="msg-meta">
          {/* Em GRUPO o nome aparece nos dois sentidos: várias pessoas
              escrevem, e sem ele a thread vira um monólogo sem autor. No 1:1
              continua só na saída — do outro lado há uma pessoa só, já
              nomeada no cabeçalho. */}
          {(daClinica || ehGrupo) && remetente?.nome ? `${remetente.nome} · ` : ''}
          {hora(mensagem.enviadaEm)}
          {daClinica && ` ${STATUS_ENVIO[mensagem.status] ?? ''}`}
          {mensagem.simulada && <span className="msg-simulada">simulada</span>}
        </span>

        {/* Aparece no hover e no foco por teclado (`:focus-within` no CSS): um
            botão que só existe no mouse deixa a ação inalcançável por Tab. */}
        <div className="msg-acoes">
          <BotaoReagir mensagem={mensagem} />
          <button
            type="button"
            className="msg-encaminhar"
            onClick={() => aoEncaminhar(mensagem)}
            title="Encaminhar esta mensagem"
            aria-label="Encaminhar mensagem"
          >
            ↪
          </button>
        </div>
      </div>
    </li>
  );
}

export default function Thread() {
  const { clinicaId, conversa } = useConversas();
  const [mensagens, setMensagens] = useState([]);
  const [erro, setErro] = useState(null);
  const [encaminhando, setEncaminhando] = useState(null);
  // ★ WB: qual mensagem está sendo reenviada agora (o botão da bolha).
  const [reenviando, setReenviando] = useState(null);
  const fim = useRef(null);

  useEffect(() => {
    setMensagens([]);
    setErro(null);
    if (!conversa) return undefined;
    return ouvirMensagens(clinicaId, conversa.id, setMensagens, (e) => {
      console.error('[OdontoSDR] Falha ao ouvir mensagens:', e);
      setErro('Não foi possível carregar o histórico desta conversa.');
    });
  }, [clinicaId, conversa?.id]);

  /** ★ WB — tentar de novo: a callable reaproveita a MESMA bolha; o listener traz o resultado. */
  async function reenviar(mensagem) {
    setErro(null);
    setReenviando(mensagem.id);
    try {
      await reenviarMensagem({ clinicaId, conversaId: conversa.id, mensagemId: mensagem.id });
    } catch (e) {
      console.error('[OdontoSDR] Falha ao reenviar:', e);
      setErro(e?.message ?? 'Não foi possível reenviar.');
    } finally {
      setReenviando(null);
    }
  }

  useEffect(() => {
    fim.current?.scrollIntoView({ block: 'end' });
  }, [mensagens.length]);

  if (!conversa) return null;

  return (
    <div className="thread">
      {erro && <p className="erro">{erro}</p>}
      {mensagens.length === 0 && !erro && (
        <p className="vazio">Nenhuma mensagem nesta conversa ainda.</p>
      )}
      <ul className="mensagens">
        {/* A regra de QUANDO quebrar o dia mora em `conversas/dias.js`; aqui só
            se desenha. A divisória é um <li> porque um <div> solto dentro de
            <ul> é HTML inválido — e leitor de tela anuncia a lista errada. */}
        {comDivisorias(mensagens).map((item) =>
          item.tipo === 'dia' ? (
            <li key={item.chave} className="msg-dia">
              <span>{item.rotulo}</span>
            </li>
          ) : (
            <Bolha
              key={item.chave}
              mensagem={item.mensagem}
              aoEncaminhar={setEncaminhando}
              ehGrupo={Boolean(conversa.ehGrupo)}
              contato={{ foto: conversa.contatoFoto, nome: nomeDoContato(conversa) }}
              aoReenviar={reenviar}
              reenviando={reenviando === item.mensagem.id}
            />
          ),
        )}
      </ul>
      <div ref={fim} />

      {encaminhando && (
        <DialogoEncaminhar
          mensagem={encaminhando}
          aberto={Boolean(encaminhando)}
          aoFechar={() => setEncaminhando(null)}
        />
      )}
    </div>
  );
}
